import { ReactElement, useMemo } from 'react';

import styled from 'styled-components';

import { Alert } from '@components/alert';
import { createForm, useCreateFormStore } from '@components/form';
import { FormLabel } from '@components/form-label';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconProps } from '@components/icons';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconOutput } from '@components/icons/output';
import { IconTextFormatLinkOff } from '@components/icons/text-format-link-off';
import { IconTrash } from '@components/icons/trash';
import { InfoBox } from '@components/info-box';
import { NoResultCard } from '@components/no-result-card';
import { Select } from '@components/select';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingEditorValue } from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { AttributeType } from 'api/attributes/requests';
import { CompanyUserOptions } from 'api/authentication/requests';
import { useJourneyAttributes, useJourneys } from 'api/journeys/hooks';
import {
  JourneyActionDetailed,
  JourneyField,
  TravellerJourneyActionDetailed,
  VariableValue,
} from 'api/types';
import { useUsers } from 'api/users/hooks';
import {
  ActionFooter,
  DescriptionFormField,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';

import {
  BaseContentProps,
  FieldMappedAttributeField,
  InfoBoxClosable,
  InfoBoxVariant,
  getFieldMappedAttributeFieldComparatorIds,
} from '../common';
import { getFieldMappingId } from '../helpers';

type Data = {
  description: string;
  journeyId: string;
  startJourney: boolean;
  waitUntilComplete: boolean;
  travellerName: VariableValue;
  travellerEmail: VariableValue;
  attributes: { [key: string]: VariableValue | VariableValue[] | string | string[] | undefined };
};

const Form = createForm<Data>();

type AvailableActionTypes = 'add_to_journey';

type AddToJourneyFormContentProps<T extends AvailableActionTypes> = BaseContentProps<
  JourneyActionDetailed<T> | TravellerJourneyActionDetailed<T>
> & {
  originalJourney: string;
  filePath: string;
  onSubmit({
    values,
    description,
  }: {
    values: {
      journeyId: string;
      startJourney: boolean;
      waitUntilComplete: boolean;
      fields: JourneyField[];
      travellerName: VariableValue;
      travellerEmail: VariableValue;
    };
    description: string;
  }): Promise<any>;
  onCancel(): void;
  hideActionButtons?: boolean;
};

const HiddenContent = styled.div<{ hidden: boolean }>((p) => ({
  visibility: p.hidden ? 'hidden' : 'visible',
}));

export const AddToJourneyFormContent = <T extends AvailableActionTypes>({
  originalJourney,
  action,
  filePath,
  onCancel,
  onSubmit,
  hideActionButtons,
}: AddToJourneyFormContentProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const formValues = formStore.useFormValues();
  const isDirty = formStore.useIsDirty();
  const journeysQuery = useJourneys('?ordering=title');
  const journeyId = formValues['journeyId'] as string;
  const startJourney = formValues['startJourney'] as boolean;
  const waitUntilComplete = formValues['waitUntilComplete'] as boolean;
  const attributeTemplatesQuery = useJourneyAttributes(journeyId ?? '');
  const attributes = (attributeTemplatesQuery.data ?? []).filter((attribute) => attribute.isActive);

  const journeyList = (journeysQuery.data ?? [])
    .filter((journey) => journey.hasPublishedJourney)
    .map((journey) => ({
      value: journey.shortId,
      label: journey.title,
      helpText: journey.description || t('noDescription'),
    }));

  const handleSubmit = () => {
    const fields = attributes.map((attribute) => {
      const { valueId } = getFieldMappedAttributeFieldComparatorIds(attribute.shortId);
      return {
        id: attribute.shortId,
        field: attribute.shortId,
        value: formValues[valueId],
        lastSavedTitle: attribute.title,
        lastSavedType: attribute.attributeType,
        lastSavedOptions: attribute.options,
      } as JourneyField;
    });

    const values = {
      journeyId: journeyId,
      lastSavedTitle: journeyList.find((journey) => journey.value === journeyId)?.label,
      startJourney: startJourney,
      waitUntilComplete: waitUntilComplete,
      travellerName: formValues['travellerName'] as VariableValue,
      travellerEmail: formValues['travellerEmail'] as VariableValue,
      fields,
    };
    return onSubmit({ values, description: formValues['description'] as string });
  };

  const clearDeletedJourney = () => {
    formStore.actions.setValue('journeyId', '');
  };

  const journey = journeyList.find((journey) => journey.value === journeyId);

  const hideDropdown = !!journeyId && !journey && !journeysQuery.isLoading;

  const variableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: originalJourney,
    types: ['text'],
  });
  const emailVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: originalJourney,
    types: ['email'],
  });
  const fieldMappingId = getFieldMappingId(journeyId);
  const defaultTravellerName: FieldMappingEditorValue = {
    value: '{{ company_traveller_name }}',
    variables: {
      company_traveller_name: {
        type: 'text',
        field: 'name',
        label: 'Full name',
        action: 'undefined',
        source: 'company_traveller',
      },
    },
  };
  const defaultTravellerEmail: FieldMappingEditorValue = {
    value: '{{ traveller_email }}',
    variables: {
      traveller_email: {
        type: 'email',
        field: 'email',
        label: 'Traveller email',
        action: 'undefined',
        source: 'traveller',
      },
    },
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSubmit}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('travellerDetails')}>
          <Form.FieldMappingTextEditor
            id="travellerName"
            label={t('travellerName')}
            validators={['required']}
            defaultValue={action.values?.travellerName ?? defaultTravellerName}
            fieldMappingId={fieldMappingId}
            plaintext
            singleLine
            variableGroups={variableGroups}
          />
          <Form.EmailField
            id="travellerEmail"
            label={t('travellerEmail')}
            validators={['required', 'email']}
            defaultValue={action.values?.travellerEmail ?? defaultTravellerEmail}
            fieldMappingId={fieldMappingId}
            plaintext
            singleLine
            variableGroups={emailVariableGroups}
          />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('journeyDetails')}>
          <Stack space={4}>
            <AddToJourneyInfoBoxClosable infoType="selectJourney" />
            <HiddenContent hidden={hideDropdown}>
              <Form.Select
                // Force a rerender when a journey is deleted that is being used
                key={`${journeyList.length}-${action.values?.journeyId}`}
                id="journeyId"
                label={t('journey')}
                options={journeyList}
                defaultValue={action.values?.journeyId}
                isClearable={false}
                validators={['required']}
              />
            </HiddenContent>
            {hideDropdown && (
              <Stack space={2}>
                <Alert variant="info">{t('journeyNotFoundInfo')}</Alert>
                <Stack space={2} orientation="horizontal">
                  <div style={{ flexGrow: 1 }}>
                    <Select
                      key={`not-found-${Boolean(hideDropdown)}`}
                      label={t('journey')}
                      defaultValue="0"
                      isDisabled
                      options={[
                        {
                          value: '0',
                          label: (
                            <Stack orientation="horizontal" space={1}>
                              <IconTextFormatLinkOff color="red100" />
                              <Text
                                color="red100"
                                text={action.values?.lastSavedTitle ?? t('journeyNotFound')}
                              />
                            </Stack>
                          ),
                        },
                      ]}
                    />
                  </div>
                  <IconButton
                    icon={<IconTrash />}
                    variant="secondary-purple"
                    onClick={clearDeletedJourney}
                    style={{ marginTop: pxToRem(28) }}
                  />
                </Stack>
              </Stack>
            )}
          </Stack>
        </HideContainer>
        {!hideDropdown && journeyId && !attributeTemplatesQuery.isLoading && (
          <>
            <Sidebar.Separator />
            <HideContainer title={t('fields')}>
              <JourneyFieldsForm
                // Force a rerender when an attribute is deleted that is being used
                key={`${journeyId}-${attributes.map((attribute) => attribute.shortId).join(',')}`}
                action={action}
                journeyId={originalJourney}
                attributes={attributes}
                filePath={filePath}
              />
            </HideContainer>
            <Sidebar.Separator />
            <HideContainer title={t('configuration')}>
              <Stack space={4}>
                <Stack space={4} orientation="horizontal" align="center" justify="space-between">
                  <Stack>
                    <FormLabel text={t('startWhenAdded')} />
                    {startJourney ? (
                      <Text text={t('core.yes')} font="regular14" />
                    ) : (
                      <Text color="purple50" text={t('core.no')} font="regular14" />
                    )}
                  </Stack>
                  <Form.Switch
                    id="startJourney"
                    label={t('startWhenAdded')}
                    labelVisible={false}
                    defaultValue={action.values?.startJourney ?? false}
                  />
                </Stack>
                <Stack space={4} orientation="horizontal" align="center" justify="space-between">
                  <Stack>
                    <FormLabel text={t('waitUntilComplete')} />
                    {waitUntilComplete ? (
                      <Text text={t('core.yes')} font="regular14" />
                    ) : (
                      <Text color="purple50" text={t('core.no')} font="regular14" />
                    )}
                  </Stack>
                  <Form.Switch
                    id="waitUntilComplete"
                    label={t('waitUntilComplete')}
                    labelVisible={false}
                    defaultValue={action.values?.waitUntilComplete ?? false}
                  />
                </Stack>
              </Stack>
            </HideContainer>
            <AddToJourneyInfoBox infoType="output" />
          </>
        )}
      </Stack>
      <ActionFooter
        action={action}
        isDisabled={!isDirty}
        hideActionButtons={hideActionButtons}
        onCancel={onCancel}
        form={Form}
      />
    </FormWithUnsavedChangesPrompt>
  );
};
type JourneyFieldsFormProps<T extends AvailableActionTypes> = BaseContentProps<
  JourneyActionDetailed<T> | TravellerJourneyActionDetailed<T>
> & {
  journeyId: string;
  attributes: AttributeType[];
  filePath: string;
};

const JourneyFieldsForm = <T extends AvailableActionTypes>({
  action,
  journeyId,
  attributes,
  filePath,
}: JourneyFieldsFormProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const initialFields = useMemo(() => {
    const savedFields = action.values?.fields ?? [];
    return (attributes ?? []).map((attribute) => ({
      id: attribute.shortId,
      field: attribute.shortId,
      value: savedFields.find((field) => field.field === attribute.shortId)?.value,
      attribute,
    }));
  }, [attributes, action.values?.fields]);

  const userQuery = useUsers();
  const usersList = userQuery.data;
  const variableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    excludedTypes: ['file', 'file_multi'],
  });
  const emailVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['email'],
  });
  const phoneVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['phone'],
  });
  const dateVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['date'],
  });
  const fileVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['file'],
  });
  const fileMultiVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['file_multi', 'file'],
  });
  const optionsVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['select'],
  });
  const numberVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['number'],
  });
  const decimalVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId,
    types: ['number', 'decimal'],
  });
  if (!attributes || attributes.length === 0) {
    return <NoResultCard icon={IconAttributeJourney} text={t('noJourneyAttributes')} />;
  }
  return (
    <Stack space={4}>
      <AddToJourneyInfoBoxClosable infoType="fields" />
      {initialFields.map((field) => {
        return (
          <FieldMappedAttributeField
            key={`${field.id}-${JSON.stringify(field.value)}`}
            field={field}
            attribute={field.attribute}
            usersList={usersList}
            form={Form}
            variableGroups={variableGroups}
            emailVariableGroups={emailVariableGroups}
            phoneVariableGroups={phoneVariableGroups}
            dateVariableGroups={dateVariableGroups}
            optionsVariableGroups={optionsVariableGroups}
            fileVariableGroups={fileVariableGroups}
            fileMultiVariableGroups={fileMultiVariableGroups}
            numberVariableGroups={numberVariableGroups}
            decimalVariableGroups={decimalVariableGroups}
            filePath={filePath}
            journeyId={journeyId}
          />
        );
      })}
    </Stack>
  );
};

type InfoType = 'selectJourney' | 'fields' | 'output' | 'fieldMissing';

const TYPE_TO_VARIANT: Record<InfoType, InfoBoxVariant> = {
  selectJourney: 'blue',
  fields: 'blue',
  output: 'indigo',
  fieldMissing: 'orange',
};

const TYPE_TO_TEXT: Record<InfoType, keyof typeof locale.en> = {
  selectJourney: 'infoSelectJourney',
  fields: 'infoFields',
  output: 'infoOutput',
  fieldMissing: 'infoFieldMissing',
};

type CloseableInfoType = 'selectJourney' | 'fields';

const TYPE_TO_OPTION_KEY: Record<CloseableInfoType, keyof CompanyUserOptions> = {
  selectJourney: 'hasHiddenAddToJourneySelectJourney',
  fields: 'hasHiddenAddToJourneyFields',
};

const TYPE_TO_ICON: Record<InfoType, ReactElement<IconProps>> = {
  selectJourney: <IconJourneyItem />,
  fields: <IconAttributeJourney />,
  output: <IconOutput />,
  fieldMissing: <IconFormIndicatorWarning />,
};

export const AddToJourneyInfoBox = ({
  infoType,
  children,
}: {
  infoType: InfoType;
  children?: ReactElement;
}) => {
  const { t } = useScopedTranslate(locale);
  const variant = TYPE_TO_VARIANT[infoType];
  const text = t(TYPE_TO_TEXT[infoType]);
  const icon = TYPE_TO_ICON[infoType];
  return <InfoBox variant={variant} text={text} icon={icon} children={children} />;
};

export const AddToJourneyInfoBoxClosable = ({ infoType }: { infoType: CloseableInfoType }) => {
  const { t } = useScopedTranslate(locale);
  const optionKey = TYPE_TO_OPTION_KEY[infoType];
  const variant = TYPE_TO_VARIANT[infoType];
  const text = t(TYPE_TO_TEXT[infoType]);
  const icon = TYPE_TO_ICON[infoType];
  return <InfoBoxClosable optionKey={optionKey} variant={variant} text={text} icon={icon} />;
};

const locale = {
  en: {
    actionDetails: 'Action details',
    journey: 'Journey',
    journeyDetails: 'Journey details',
    infoSelectJourney: 'Select which journey you want to add this traveller to',
    noDescription: 'No description',
    noJourneyAttributes: 'There are no journey fields for this journey',
    journeyNotFoundInfo:
      "The selected journey has either been removed or is in a workspace you don't have access to",
    fields: 'Fields',
    infoFields: 'Add field information you want to move over with the traveller',
    infoOutput: 'When this action is run the traveller will be added to the selected journey',
    configuration: 'Configuration',
    startWhenAdded: 'Start journey when added',
    waitUntilComplete: 'Wait until journey completes',
    continueOnCompletion: 'Continue on completion',
    journeyNotFound: 'Journey not found',
    infoFieldMissing:
      'The following fields are missing from the action setup. Edit the action to add their values.',
    travellerDetails: 'Traveller details',
    travellerName: 'Traveller name',
    travellerEmail: 'Traveller email',
  },
};
